import type { CSSProperties, ElementType, FC, ReactNode } from 'react'

import { Fragment } from 'react'

import { cn } from '@/lib/utils'
import { SplitText } from '@/components/generals/split-text'

export interface SplitRun {
    text: string
    className?: string
}

interface JustifiedSplitProps {
    /* Consecutive runs of the same sentence. Splitting a heading into runs is
       what lets a single justified paragraph carry a coloured phrase without
       breaking the per-character stagger that sweeps across all of it. */
    runs: SplitRun[]
    as?: ElementType
    className?: string
    style?: CSSProperties
    /* Leading indent in character widths, rendered as hard spaces exactly the
       way the reference indents its opening line. */
    indent?: number
    mode?: 'chars' | 'words'
    stagger?: number
    delay?: number
    duration?: number
    trigger?: 'scroll' | 'mount' | 'none'
    start?: string
    justify?: boolean
    id?: string
}

/* U+00A0. Built from its code point so the source stays plain ASCII and the
   indent can never be collapsed away by whitespace processing. */
const HARD_SPACE = String.fromCharCode(160)

export const JustifiedSplit: FC<JustifiedSplitProps> = ({
    runs,
    as: Tag = 'h2',
    className,
    style,
    indent = 0,
    mode = 'chars',
    stagger = 0.012,
    delay = 0,
    duration = 1.05,
    trigger = 'scroll',
    start = 'top 85%',
    justify = true,
    id
}): ReactNode => {
    const sentence = runs.map((run) => run.text).join(' ')

    /* Each run picks up where the previous one left off so the glyphs read as
       one continuous sweep rather than several separate reveals. */
    let elapsed = delay

    return (
        <Tag
            id={id}
            className={cn(justify ? 'text-justify' : null, className)}
            style={style}
        >
            <span className='sr-only'>
                {sentence}
            </span>

            <span aria-hidden='true'>
                {indent > 0 ? HARD_SPACE.repeat(indent) : null}

                {runs.map((run, runIndex) => {
                    const runDelay = elapsed

                    elapsed += Array.from(run.text).length * stagger * 0.5

                    return (
                        <Fragment key={`${run.text}-${runIndex}`}>
                            {runIndex > 0 ? ' ' : null}

                            <SplitText
                                text={run.text}
                                as='span'
                                mode={mode}
                                stagger={stagger}
                                delay={runDelay}
                                duration={duration}
                                trigger={trigger}
                                start={start}
                                className={cn('inline', run.className)}
                            />
                        </Fragment>
                    )
                })}
            </span>
        </Tag>
    )
}
